import React, { useState } from 'react';
import { AppBar, Box, CssBaseline, GlobalStyles, IconButton, Toolbar, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Sidebar from './Sidebar';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CancelIcon from '@mui/icons-material/Cancel';
import TodayIcon from '@mui/icons-material/Today';
import LinkIcon from '@mui/icons-material/Link';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InventoryIcon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupIcon from '@mui/icons-material/Group';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { canAccess, canAccessAny, NAV_PERMISSIONS } from '../../utils/permissions';

const HIDDEN_NAV_PATHS = new Set([
  '/daily-work-cost-sheet-mapping',
  '/project-expenses',
  '/project-timelines',
]);

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout, switchOrganisation } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [switchingOrganisation, setSwitchingOrganisation] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  const handleSwitchOrganisation = async (organisationId) => {
    if (!organisationId || organisationId === user?.organisationId) {
      return;
    }

    setSwitchingOrganisation(true);
    try {
      await switchOrganisation(organisationId);
    } catch (error) {
      setSwitchingOrganisation(false);
    }
  };

  const allMenuItems = [
    { label: t('nav.dashboard'), path: '/', icon: <DashboardIcon /> },
    { label: t('nav.companies'), path: '/companies', icon: <BusinessIcon /> },
    { label: t('nav.projects'), path: '/projects', icon: <AssignmentIcon /> },
    { label: t('nav.generalTasks'), path: '/general-tasks', icon: <ChecklistIcon /> },
    { label: t('nav.personnel'), path: '/personnel', icon: <PeopleIcon /> },
    { label: t('nav.dueDates'), path: '/due-dates', icon: <EventBusyIcon /> },
    { label: t('nav.finance'), path: '/finance', icon: <DescriptionIcon /> },
    { label: t('nav.warehouse'), path: '/warehouse', icon: <InventoryIcon /> },
    { label: t('nav.siteDelivery'), path: '/site-delivery', icon: <LocalShippingIcon /> },
    { label: t('nav.siteDeparture'), path: '/site-departure', icon: <ExitToAppIcon /> },
    { label: t('nav.costSheets'), path: '/cost-sheets', icon: <ReceiptIcon /> },
    { label: t('nav.dailyWorkInput'), path: '/daily-work-input', icon: <TodayIcon /> },
    { label: t('nav.dailyWorkCostSheetMapping'), path: '/daily-work-cost-sheet-mapping', icon: <LinkIcon /> },
    { label: t('nav.workerHoursReport'), path: '/worker-hours-report', icon: <ScheduleIcon /> },
    { label: t('nav.myHours'), path: '/my-hours', icon: <AccessTimeIcon /> },
    { label: t('nav.workQuantities'), path: '/work-quantities', icon: <AssessmentIcon /> },
    { label: t('nav.projectExpenses'), path: '/project-expenses', icon: <AttachMoneyIcon /> },
    { label: t('nav.projectTimelines'), path: '/project-timelines', icon: <TimelineIcon /> },
    { label: t('nav.writeOffs'), path: '/write-offs', icon: <CancelIcon /> },
    { label: t('nav.users'), path: '/users', icon: <GroupIcon /> },
    { label: t('nav.account'), path: '/account', icon: <AccountCircleIcon /> },
  ];

  const menuItems = allMenuItems.filter((item) => {
    if (HIDDEN_NAV_PATHS.has(item.path)) {
      return false;
    }
    if (item.path === '/my-hours' && !user?.personnel) {
      return false;
    }
    const permission = NAV_PERMISSIONS[item.path];
    if (!permission) {
      return true;
    }
    if (Array.isArray(permission)) {
      return canAccessAny(user, permission);
    }
    return canAccess(user, permission);
  });

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          '@media (max-width:899.95px)': {
            'input[type=number]::-webkit-outer-spin-button,input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            'input[type=number]': {
              MozAppearance: 'textfield',
            },
          },
        }}
      />
      <AppBar
        position="fixed"
        color="primary"
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
            aria-label="open navigation menu"
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {t('app.name')}
          </Typography>
        </Toolbar>
      </AppBar>
      <Sidebar
        menuItems={menuItems}
        mobileOpen={mobileOpen}
        onMobileClose={handleMobileClose}
        userEmail={user?.email}
        organisationName={user?.organisationName}
        memberships={user?.memberships || []}
        activeOrganisationId={user?.organisationId}
        switchingOrganisation={switchingOrganisation}
        onSwitchOrganisation={handleSwitchOrganisation}
        onLogout={logout}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: { xs: 7, md: 0 },
          minWidth: 0, // Allow flex item to shrink so overflow scroll works
          maxWidth: '100%',
          overflowX: 'hidden', // Keep horizontal scroll inside timeline container only
          overflowY: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout; 